import { Alert, Radio, Switch } from 'antd';
import Page from 'components/Page';
import SquidlerMark from 'assets/images/squidler-mark.svg';
import useDataSource from 'hooks/useDataSource';
import React from 'react';
import { Link } from 'react-router-dom';
import { EDIT_LICENSE } from 'shared/constants/permissions';
import { hasAccessibilityLicense } from 'shared/utils/license';
import { useAccessibilityReview } from './context';
import { PAGE_TYPE, pageLimitAlertPresentation } from './viewModel';

export const SquidlerAttribution = () => (
  <a href="https://squidler.io" target="_blank" rel="noopener noreferrer" aria-label="Powered by Squidler (opens in a new tab)" style={{ alignItems: 'center', color: '#8c8c8c', display: 'inline-flex', fontSize: 12, fontWeight: 500, gap: 6, whiteSpace: 'nowrap' }}>
    <span>Powered by</span>
    <img src={SquidlerMark} alt="Squidler" style={{ borderRadius: 3, display: 'block', height: 18, width: 18 }} />
    <span style={{ color: '#0086bf', fontWeight: 600 }}>Squidler</span>
  </a>
);

export const REVIEW_CLASS = 'module-accessibility';

export const ReviewPage = ({ title, children }) => (
  <Page>
    <Page.Header title={title} extra={<SquidlerAttribution />} />
    <ReviewAlerts />
    {children}
  </Page>
);

export const PageIntroduction = ({ title, children }) => (
  <div style={{ marginBottom: 16 }}>
    <h3 style={{ margin: '0 0 6px' }}>{title}</h3>
    <div>{children}</div>
  </div>
);

export const InactiveNotice = () => {
  const { baseUrl } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const licensed = hasAccessibilityLicense(dataSource?.activeLicense);

  if (!licensed) {
    return (
      <div style={{ marginBottom: 16 }}>
        Accessibility is not included in the active license.
        {' '}
        Super users can activate manual checks from the
        {' '}
        <Link to={`${baseUrl}/overview`}>overview</Link>
        .
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      Activate accessibility from the
      {' '}
      <Link to={`${baseUrl}/overview`}>overview</Link>
      {' '}
      to start scanning.
    </div>
  );
};

export const ReviewAlerts = () => {
  const { result, scope } = useAccessibilityReview();
  const { dataSourceId, hasDataSourcePermission } = useDataSource();
  const latest = result?.latestSync
    || scope?.scanOverview?.sources?.find(item => item.source === 'PROVIDER')?.latestRun
    || scope?.syncs?.[0];
  const failed = String(latest?.status || '').toLowerCase() === 'failed';
  const pageLimitAlert = pageLimitAlertPresentation({
    pageLimit: scope?.scanOverview?.pageLimit,
    htmlPageCount: scope?.scanOverview?.htmlPageCount,
  });
  const canEditLicense = Boolean(hasDataSourcePermission(EDIT_LICENSE));
  if (!failed && !pageLimitAlert) return null;

  return (
    <>
      {failed && (
        <Alert
          type="error"
          showIcon
          message="The latest provider import failed."
          description="The checklist and findings continue to show the last complete import."
        />
      )}
      {pageLimitAlert && (
        <Alert
          type="info"
          showIcon
          message={pageLimitAlert.message}
          description={(
            <>
              {pageLimitAlert.description}
              {' '}
              {canEditLicense ? (
                <Link to={`/data-sources/${dataSourceId}/settings/license`}>Upgrade the license</Link>
              ) : (
                'Ask the owner to upgrade the license'
              )}
              {' '}
              to include more pages.
            </>
          )}
        />
      )}
    </>
  );
};

const pageTypeOptionLabel = (label, count) => (typeof count === 'number' ? `${label} (${count})` : label);

export const PageTypeFilter = ({ counts, style } = {}) => {
  const { pageType, setPageType } = useAccessibilityReview();

  return (
    <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16, ...style }}>
      <Radio.Group
        aria-label="Page type"
        buttonStyle="solid"
        value={pageType || PAGE_TYPE.ALL}
        onChange={event => setPageType(event.target.value)}
        style={{ display: 'flex', flexWrap: 'wrap' }}
      >
        <Radio.Button value={PAGE_TYPE.ALL}>{pageTypeOptionLabel('All', counts?.total)}</Radio.Button>
        <Radio.Button value={PAGE_TYPE.HTML}>{pageTypeOptionLabel('HTML', counts?.html)}</Radio.Button>
        <Radio.Button value={PAGE_TYPE.PDF}>{pageTypeOptionLabel('PDF', counts?.pdf)}</Radio.Button>
      </Radio.Group>
    </div>
  );
};

export const HiddenFindingsFilter = ({ style } = {}) => {
  const { includeHiddenDispositions, setIncludeHiddenDispositions } = useAccessibilityReview();
  return (
    <label style={{ alignItems: 'center', display: 'inline-flex', gap: 8, marginBottom: 16, ...style }}>
      <Switch
        aria-label="Show muted and dismissed findings"
        checked={Boolean(includeHiddenDispositions)}
        onChange={setIncludeHiddenDispositions}
        size="small"
      />
      Show muted and dismissed
    </label>
  );
};

export const TABLE_SCROLL = { x: 'max-content' };
